import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, ImageIcon, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import ImageUpload from "./ImageUpload";
import RichTextEditor from "./RichTextEditor";
import TemplatePicker from "./TemplatePicker";
import { shorthandToHtml } from "../../lib/richText";

export default function CrudManager({ label, api, fields, renderRow, makeEmpty, banner, rowExtra, reorderable, onReorder }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.list().then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  const toFormValue = (item) => {
    const f = { ...item };
    fields.forEach((fl) => {
      if (fl.type === "lines") f[fl.name] = (item[fl.name] || []).join("\n");
      if (fl.type === "select" && !item[fl.name]) f[fl.name] = "none";
      if (fl.type === "richtext" && !item[fl.name] && fl.convertFrom && (item[fl.convertFrom] || []).length) {
        f[fl.name] = shorthandToHtml((item[fl.convertFrom] || []).join("\n"));
      }
    });
    return f;
  };

  const openCreate = () => { setEditing(null); setForm(makeEmpty()); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm(toFormValue(item)); setOpen(true); };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((fl) => {
        if (fl.type === "lines") payload[fl.name] = (form[fl.name] || "").split("\n").map((s) => s.trim()).filter(Boolean);
        if (fl.type === "number") payload[fl.name] = Number(form[fl.name] || 0);
        if (fl.type === "select" && payload[fl.name] === "none") payload[fl.name] = "";
      });
      if (editing) await api.update(editing.id, payload);
      else await api.create(payload);
      toast({ title: `${label} ${editing ? "updated" : "created"}` });
      setOpen(false);
      load();
    } catch (e) {
      toast({ title: "Error saving", description: e?.response?.data?.detail || "Please try again", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.remove(deleteId);
      toast({ title: `${label} deleted` });
      load();
    } catch (e) {
      toast({ title: "Error deleting", variant: "destructive" });
    } finally {
      setDeleteId(null);
    }
  };

  const move = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    try {
      await onReorder(next.map((it) => it.id));
    } catch (e) {
      toast({ title: "Couldn't save order", variant: "destructive" });
      load();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">{label}s</h1>
          <p className="text-sm text-slate-500">{items.length} total</p>
        </div>
        <Button onClick={openCreate} className="bg-orange hover:bg-orange-dark text-white rounded-full">
          <Plus size={16} className="mr-1.5" /> New {label}
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400">Loading...</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">No {label.toLowerCase()}s yet. Create one!</div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => {
            const row = renderRow(item);
            return (
              <div key={item.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
                {reorderable && (
                  <div className="flex flex-col shrink-0 -my-1">
                    <button data-testid={`move-up-${item.id}`} onClick={() => move(index, -1)} disabled={index === 0} title="Move up" className="w-7 h-6 rounded-t-md bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-navy"><ChevronUp size={15} /></button>
                    <button data-testid={`move-down-${item.id}`} onClick={() => move(index, 1)} disabled={index === items.length - 1} title="Move down" className="w-7 h-6 rounded-b-md bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-navy border-t border-white"><ChevronDown size={15} /></button>
                  </div>
                )}
                {row.image !== undefined && (
                  row.image ? <img loading="lazy" decoding="async" src={row.image} alt="" className="w-16 h-12 rounded-lg object-cover shrink-0" />
                    : <div className="w-16 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><ImageIcon size={18} className="text-slate-300" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-navy truncate">{row.title}</p>
                  {row.subtitle && <p className="text-sm text-slate-400 truncate">{row.subtitle}</p>}
                </div>
                {row.badge && (() => {
                  const b = typeof row.badge === "string" ? { text: row.badge } : row.badge;
                  const tone = {
                    green: "bg-green-100 text-green-700",
                    amber: "bg-amber-100 text-amber-700",
                    slate: "bg-slate-100 text-slate-600",
                  }[b.tone] || "bg-slate-100 text-slate-600";
                  return <span data-testid={`row-badge-${item.id}`} className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${tone}`}>{b.text}</span>;
                })()}
                <div className="flex gap-2 shrink-0">
                  {rowExtra && rowExtra(item, load)}
                  <button onClick={() => openEdit(item)} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy transition-colors"><Pencil size={15} /></button>
                  <button onClick={() => setDeleteId(item.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${label}` : `New ${label}`}</DialogTitle>
          </DialogHeader>
          {banner && banner(form)}
          <div className="space-y-4 py-2">
            {fields.filter((fl) => !(fl.createOnly && editing)).map((fl) => (
              <div key={fl.name} className={fl.type === "switch" ? "flex items-center justify-between" : ""}>
                {fl.type === "image" ? (
                  <ImageUpload
                    label={fl.label}
                    value={form[fl.name] || ""}
                    onChange={(v) => setField(fl.name, v)}
                    {...(fl.alt ? { altValue: form[fl.alt] || "", onAltChange: (v) => setField(fl.alt, v) } : {})}
                  />
                ) : (
                <>
                <Label className="text-navy font-semibold">{fl.label}</Label>
                {fl.type === "textarea" || fl.type === "lines" ? (
                  <Textarea rows={fl.rows || 4} value={form[fl.name] || ""} onChange={(e) => setField(fl.name, e.target.value)}
                    placeholder={fl.placeholder} className="mt-2" />
                ) : fl.type === "richtext" ? (
                  <div className="mt-2">
                    {fl.templateKind && (
                      <div className="mb-2">
                        <TemplatePicker
                          kind={fl.templateKind}
                          hasContent={() => !!(form[fl.name] || "").trim()}
                          onApply={(t) => setField(fl.name, t.body || "")}
                        />
                      </div>
                    )}
                    <RichTextEditor testId={`rte-${fl.name}`} value={form[fl.name] || ""} onChange={(v) => setField(fl.name, v)} />
                  </div>
                ) : fl.type === "select" ? (
                  <Select value={form[fl.name] || ""} onValueChange={(v) => setField(fl.name, v)}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder={fl.placeholder || "Select"} /></SelectTrigger>
                    <SelectContent>
                      {fl.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : fl.type === "switch" ? (
                  <Switch checked={!!form[fl.name]} onCheckedChange={(v) => setField(fl.name, v)} />
                ) : (
                  <Input type={fl.type === "number" ? "number" : fl.type === "date" ? "date" : "text"} value={form[fl.name] ?? ""} onChange={(e) => setField(fl.name, e.target.value)}
                    placeholder={fl.placeholder} className="mt-2" />
                )}
                </>
                )}
                {fl.hint && <p className="text-xs text-slate-400 mt-1">{fl.hint}</p>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full"><X size={15} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full">
              <Save size={15} className="mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {label.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
